import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { getPublisherTagInstance } from '../utils/getPublisherTagInstance.ts';
import { loadGpt } from '../utils/gptLoader.ts';
import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';
import { type AdSizes, type SlotRenderEndedEvent } from '../types.ts';
import { type OnAbsentAd } from '../hooks/useAbsentAdHandler.ts';

export type AdUnitProps = {
    adId: string;
    elementId: string;
    width: number;
    height: number;
    sizes?: AdSizes;
    heightHold?: number;
    widthHold?: number;
    className?: string;
    onSlotRenderEnded?: (event: SlotRenderEndedEvent, element: HTMLDivElement) => void;
    onAbsentAd?: OnAbsentAd;
    lazyLoad?: boolean;
    shouldCollapseOnNoFill?: boolean;
    dataTn?: string;
    testName?: string;
    testVariant?: string;
};

type GptLoadState = 'initial' | 'loaded' | 'failed';

export const AdUnit: FC<AdUnitProps> = ({
    adId,
    elementId,
    width,
    height,
    className,
    onSlotRenderEnded,
    onAbsentAd,
    lazyLoad = false,
    sizes,
    heightHold,
    widthHold,
    shouldCollapseOnNoFill = false,
    dataTn,
    testName = '',
    testVariant = '',
}) => {
    const adRef = useRef<HTMLDivElement>(null);

    const [gptLoadState, setGptLoadState] = useState<GptLoadState>('initial');
    const [adSlot, setAdSlot] = useState<googletag.Slot | null>(null);
    const [isAdSlotRendered, setIsAdSlotRendered] = useState(false);

    const triggerGptLoad = useCallback(async (): Promise<void> => {
        try {
            await loadGpt();
            setGptLoadState('loaded');
        } catch {
            setGptLoadState('failed');
            onAbsentAd?.(elementId);
        }
    }, [onAbsentAd, elementId]);

    useEffect(() => {
        return () => {
            if (adSlot) {
                const gt = getPublisherTagInstance();
                gt?.destroySlots([adSlot]);
            }
        };
    }, [adSlot]);

    useEffect(() => {
        if (!lazyLoad) {
            triggerGptLoad();
        }
    }, [lazyLoad, triggerGptLoad]);

    useEffect(() => {
        const gt = getPublisherTagInstance();
        const onSlotRenderEndedHandler = (event: SlotRenderEndedEvent): void => {
            if (adRef.current) {
                setIsAdSlotRendered(true);
                onSlotRenderEnded?.(event, adRef.current);
                if (event.isEmpty) {
                    onAbsentAd?.(event.slot.getSlotElementId());
                }
            }
        };

        gt?.pubads().addEventListener('slotRenderEnded', onSlotRenderEndedHandler);

        if (gptLoadState === 'loaded' && !adSlot) {
            gt?.cmd.push(() => {
                gt?.pubads();

                // define ad slot
                const slot = gt.defineSlot(adId, sizes ? sizes : [width, height], elementId);
                if (slot) {
                    // configure ad slot
                    slot.addService(gt.pubads());
                    slot.setConfig({
                        ...(shouldCollapseOnNoFill ? { collapseDiv: 'ON_NO_FILL' } : {}),
                        ...(testName && testVariant
                            ? { targeting: { test_name: testName, test_arm: testVariant } }
                            : {}),
                    });
                }

                if (!gt.pubadsReady) {
                    gt.pubads().enableSingleRequest();
                    gt.enableServices();
                }

                setAdSlot(slot);
            });

            gt?.cmd.push(() => {
                gt?.display(elementId);
            });
        }
        return () => {
            gt?.pubads()?.removeEventListener?.('slotRenderEnded', onSlotRenderEndedHandler);
        };
    }, [
        gptLoadState,
        adId,
        elementId,
        adSlot,
        onSlotRenderEnded,
        onAbsentAd,
        sizes,
        width,
        height,
        shouldCollapseOnNoFill,
        testName,
        testVariant,
    ]);

    useVisibilityTracker({
        elementRef: adRef,
        onVisibilityChange: ({ isVisible }) => {
            if (isVisible) {
                triggerGptLoad();
            }
        },
        options: {
            rootMargin: '200% 0px', // Render slots within 2 viewports.
        },
        isEnabled: lazyLoad && gptLoadState !== 'failed',
    });

    if (gptLoadState === 'failed') {
        return null;
    }

    return (
        <div
            style={!isAdSlotRendered ? { minHeight: heightHold || height } : {}}
            className={className}
            id={elementId}
            data-tn={dataTn || 'ad-unit'}
            data-target-width={widthHold || width}
            data-target-height={heightHold || height}
            ref={adRef}
        />
    );
};
